import classNames from 'classnames'
import React, { ComponentType, ReactNode } from 'react'

import { Accordion, AccordionItem } from '../accordion/accordion.js'
import { Button, ButtonAnchorProps, ButtonProps } from '../button/button.js'
import { Container } from '../container/container.js'
import { GridColumn } from '../grid/grid-column.js'
import { Grid } from '../grid/grid.js'
import { useCurrentBreakPoint } from '../helper/hooks/use-current-breakpoint.js'
import { scrollToElement } from '../helper/index.js'
import { ChevronRightLargeIcon, ChevronUpIcon, IconProps } from '../icon/icon.js'

export interface FooterProps {
    topNote?: FooterTopNoteProps
    columns?: Array<FooterColumnProps | ButtonProps>
    copyright?: string
    className?: string
    language?: ButtonProps
    isLisaand?: boolean
    social?: FooterSocialProps
    terms?: ButtonAnchorProps[]
    topButton?: ButtonProps
}

export interface FooterColumnProps {
    title: string
    titleLink?: string
    links: ButtonAnchorProps[] | undefined
    text?: string | ReactNode
}

export interface FooterSocialProps {
    title: string
    items: FooterSocialItemProps[]
    text?: string
}

export interface FooterSocialItemProps extends ButtonAnchorProps {
    icon: ComponentType<IconProps>
}

interface FooterTopNoteProps {
    text: string
    button: ButtonProps
}

export const Footer = (props: FooterProps): JSX.Element => {
    const bp = useCurrentBreakPoint()
    const isMobile = bp ? ['xs', 'sm', 'md'].includes(bp) : true

    const className: string = classNames('footer', { 'footer--lisaand': props.isLisaand }, props.className)

    const isColumnProps = (col: FooterColumnProps | ButtonProps): col is FooterColumnProps =>
        (col as FooterColumnProps).title !== undefined

    const renderLinks = (links: ButtonAnchorProps[]): JSX.Element => {
        return (
            <ul className="footer__list">
                {links.map((link: ButtonAnchorProps, index: number) => {
                    return (
                        <li className="footer__item" key={index}>
                            {link.url ? (
                                <Button {...link} className="footer__item-button" isBlock={true} style="link" />
                            ) : (
                                <span className="footer__item-text">{link.text}</span>
                            )}
                        </li>
                    )
                })}
            </ul>
        )
    }

    const renderColumns = (columns: Array<FooterColumnProps | ButtonProps>): JSX.Element => (
        <div className="footer__grid h-text-left">
            {columns.map((column: FooterColumnProps | ButtonProps, index: number) => {
                if (isColumnProps(column)) {
                    return (
                        <div className="footer__column" key={index}>
                            {column.titleLink ? (
                                <Button
                                    {...{
                                        url: column.titleLink,
                                        text: column.title,
                                        iconRight: ChevronRightLargeIcon,
                                        isBlock: true,
                                        className: 'footer__title-link',
                                        iconClassName: 'button__icon--large',
                                        textClassName: 'h-text-left h-w-100',
                                    }}
                                    isBlock={true}
                                    style="link"
                                />
                            ) : (
                                <strong className="footer__title text-large">{column.title}</strong>
                            )}

                            {column.text && <p className="footer__text text-small">{column.text}</p>}
                            {column.links && renderLinks(column.links)}
                        </div>
                    )
                }

                return (
                    <div className="footer__column" key={index}>
                        {renderFAQ(column)}
                    </div>
                )
            })}
        </div>
    )

    const renderAccordion = (columns: Array<FooterColumnProps | ButtonProps>): JSX.Element => (
        <Accordion variant="footer">
            {columns.map((column: FooterColumnProps | ButtonProps, index: number) => {
                if (isColumnProps(column)) {
                    if (column.titleLink) {
                        return (
                            <Button
                                {...{
                                    url: column.titleLink,
                                    text: column.title,
                                    iconRight: ChevronRightLargeIcon,
                                    isBlock: true,
                                    className: 'footer__title-link',
                                    iconClassName: 'button__icon--large',
                                    textClassName: 'h-text-left h-w-100',
                                }}
                                isBlock={true}
                                style="link"
                                key={index}
                            />
                        )
                    } else {
                        return (
                            <AccordionItem
                                id={`footer-accordion-item-${index}`}
                                label={column.title}
                                key={`footer-accordion-item-${index}`}
                            >
                                {column.text && <p className="footer__text text-small">{column.text}</p>}
                                {column.links && renderLinks(column.links)}
                            </AccordionItem>
                        )
                    }
                }

                return (
                    <div
                        className="accordion-item"
                        id={`footer-accordion-item-${index}`}
                        key={`footer-accordion-item-${index}`}
                    >
                        {renderFAQ(column)}
                    </div>
                )
            })}
        </Accordion>
    )

    const renderSocial = (social: FooterSocialProps): JSX.Element => (
        <div className="footer__social">
            <div className="footer__social-title text-tiny h-text-center">{social.title}</div>
            <Grid className="footer__social-list" align={['center-xs', 'start-lg']} gutter="mobile">
                {social.items.map((item: FooterSocialItemProps, index: number) => (
                    <GridColumn className="footer__social-item" key={index} width={['xs']}>
                        <Button {...item} iconClassName="button__icon--large" />
                    </GridColumn>
                ))}
            </Grid>
        </div>
    )

    const renderTerms = (terms: ButtonAnchorProps[]): JSX.Element => (
        <div className="footer__terms">
            <ul className="footer__terms-list">
                {terms.map((item: ButtonAnchorProps, index: number) => (
                    <li className="footer__terms-item" key={index}>
                        <Button {...item} className="footer__terms-button" style="link" />
                    </li>
                ))}
            </ul>
        </div>
    )

    const renderCopyright = (copyright: string): JSX.Element => (
        <div className="footer__copyright text-tiny">{copyright}</div>
    )

    const renderLanguage = (language: ButtonProps): JSX.Element => (
        <div className="footer__language">
            <Button {...language} className="footer__language-button" style="link" />
        </div>
    )

    const renderTopButton = (topButton: ButtonProps): JSX.Element => (
        <Button
            {...topButton}
            className="footer__scroll-button"
            iconRight={ChevronUpIcon}
            onClick={() => scrollToElement('root')}
            style="link"
        />
    )

    const renderFAQ = (faq: ButtonProps): JSX.Element => (
        <Button
            {...faq}
            className="footer__faq-button"
            iconClassName="button__icon--large"
            iconRight={!isMobile ? ChevronRightLargeIcon : undefined}
            style="link"
            isBlock={true}
        />
    )

    return (
        <footer className={className}>
            {(props.columns || props.topButton) &&
                (props.columns && isMobile ? (
                    <>
                        {props.topNote && <FooterTopNote {...props.topNote} />}
                        {renderAccordion(props.columns)}
                    </>
                ) : (
                    <Container className="h-text-right">
                        {props.topButton && renderTopButton(props.topButton)}
                        {props.topNote && <FooterTopNote {...props.topNote} />}
                        {props.columns && renderColumns(props.columns)}
                    </Container>
                ))}
            <div className="footer__bottom">
                <Container>
                    <div className="footer__bottom-inner">
                        {props.copyright && renderCopyright(props.copyright)}
                        {props.language && renderLanguage(props.language)}
                        {props.social && renderSocial(props.social)}
                        {props.terms && renderTerms(props.terms)}
                    </div>
                </Container>
            </div>
        </footer>
    )
}

const FooterTopNote = (props: FooterTopNoteProps): JSX.Element => (
    <div className="footer__top-note">
        <div className="text-small">{props.text}</div>
        <div>
            <Button className="footer__top-note-button" variant="secondary" {...props.button} />
        </div>
    </div>
)
